import { tradeRecords, type JournalEntry } from '@/mocks/tradingData';

const formatSigned = (n: number): string =>
  `${n < 0 ? '-' : '+'}${Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// 'Sep 21, 2026' -> 'SEP 21, 2026'
const formatLongDate = (date: string): string => date.toUpperCase();

// 'Sep 21, 2026' -> 'SEP 21'
const formatShortDate = (date: string): string => {
  const parts = date.split(',');
  return (parts[0] ?? date).trim().toUpperCase();
};

// Resolve the calendar year and month (0-based) that a record actually falls in,
// straight from its date string, so the month scope is read off the data itself
// rather than fixed by hand.
const parseYearMonth = (date: string): { year: number; month: number } | null => {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return null;
  return { year: parsed.getFullYear(), month: parsed.getMonth() };
};

const MONTH_FULL = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

// Derived, never stored. The journal feed is generated from the single trade
// ledger (`tradeRecords`), so every page that renders trades — the home preview,
// the how-it-works example and the full trade journal table — updates
// automatically whenever that one ledger changes. Newest entry first.
export const journalEntries: JournalEntry[] = [...tradeRecords]
  .reverse()
  .map((t) => ({
    instrument: 'XAUUSD',
    side: t.side,
    date: formatLongDate(t.date),
    entry: t.entry.toFixed(2),
    exit: t.exit.toFixed(2),
    volume: t.volume.toFixed(2),
    result: formatSigned(t.profit),
    profit: t.profit > 0,
  }));

// The most recent calendar month present in the ledger. Because the feed is
// newest-first, the head of the list defines the month. Add a trade in a later
// month and this scope simply moves forward on its own — no code change needed.
const latestMonth =
  journalEntries.length > 0 ? parseYearMonth(journalEntries[0].date) : null;

// Every entry that falls inside that latest calendar month, still newest-first,
// so a preview can show the whole current month rather than a fixed count.
export const journalMonthEntries: JournalEntry[] =
  latestMonth === null
    ? []
    : journalEntries.filter((entry) => {
        const ym = parseYearMonth(entry.date);
        return ym !== null && ym.year === latestMonth.year && ym.month === latestMonth.month;
      });

// Human-readable month scope, e.g. 'September 2026' and 'SEP 2026'.
export const journalMonthLabel =
  latestMonth === null ? '' : `${MONTH_FULL[latestMonth.month]} ${latestMonth.year}`;

export const journalMonthShortLabel =
  latestMonth === null
    ? ''
    : `${MONTH_FULL[latestMonth.month].slice(0, 3).toUpperCase()} ${latestMonth.year}`;

// Compact timeline marks for the whole current month.
export const journalTimeline = journalMonthEntries.map((entry) => ({
  date: formatShortDate(entry.date),
  side: entry.side,
  result: entry.result,
  profit: entry.profit,
}));